<template>
  <ServicePageLayout
    title="Energy Alerts"
    subtitle="ตั้งค่ามิเตอร์ที่ต้องติดตาม, กติกาแจ้งเตือน และผู้รับการแจ้งเตือน"
    icon="mdi-flash-alert"
    breadcrumb="Operations"
  >
    <v-row>
      <v-col>
        <v-card class="industrial-panel">
          <v-card-actions class="px-4 pt-4">
            <v-spacer />
            <v-btn color="primary" :loading="loading" @click="refresh">Refresh</v-btn>
          </v-card-actions>
          <v-card-text>
            <v-tabs v-model="tab" grow class="industrial-tabs">
              <v-tab>Meters to monitor</v-tab>
              <v-tab>Rules</v-tab>
              <v-tab>Recipients</v-tab>
              <v-tab>Engine runs</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <v-row class="mt-4">
                  <v-col cols="12" md="6">
                    <v-text-field v-model="search" label="Search meter" prepend-inner-icon="mdi-magnify" dense solo />
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="watchHeaders"
                  :items="filteredMeters"
                  :loading="loading"
                  item-key="assetId"
                  dense
                >
                  <template v-slot:item.enabled="{ item }">
                    <v-switch
                      :disabled="role !== 'Admin'"
                      :input-value="item.enabled"
                      inset
                      @change="toggleWatch(item, $event)"
                    />
                  </template>
                  <template v-slot:item.assetName="{ item }">
                    <div>
                      <div>{{ item.assetName }}</div>
                      <div class="text-caption grey--text">{{ item.assetId }}</div>
                    </div>
                  </template>
                </v-data-table>
              </v-tab-item>

              <v-tab-item>
                <v-row class="mt-4">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="selectedAssetId"
                      :items="meterOptions"
                      item-text="text"
                      item-value="value"
                      label="Select meter"
                      dense
                      solo
                    />
                  </v-col>
                  <v-spacer />
                  <v-col cols="12" md="3" class="text-right">
                    <v-btn color="primary" :disabled="!selectedAssetId || role !== 'Admin'" @click="addRule">
                      <v-icon left>mdi-plus</v-icon>New rule
                    </v-btn>
                  </v-col>
                </v-row>

                <v-data-table :headers="ruleHeaders" :items="rules" :loading="loadingRules" dense item-key="rowKey">
                  <template v-slot:item.enabled="{ item }">
                    <v-checkbox v-model="item.enabled" :disabled="role !== 'Admin'" @change="saveRule(item)" />
                  </template>
                  <template v-slot:item.metric="{ item }">
                    <v-select
                      v-model="item.metric"
                      :items="metricOptions"
                      :disabled="role !== 'Admin'"
                      dense
                      hide-details
                      style="max-width: 140px"
                    />
                  </template>
                  <template v-slot:item.operation="{ item }">
                    <v-select
                      v-model="item.operation"
                      :items="operationOptions"
                      :disabled="role !== 'Admin'"
                      dense
                      hide-details
                      style="max-width: 110px"
                    />
                  </template>
                  <template v-slot:item.threshold="{ item }">
                    <v-text-field
                      v-model.number="item.threshold"
                      type="number"
                      :disabled="role !== 'Admin'"
                      dense
                      hide-details
                      style="max-width: 140px"
                    />
                  </template>
                  <template v-slot:item.title="{ item }">
                    <v-text-field
                      v-model="item.title"
                      :disabled="role !== 'Admin'"
                      dense
                      hide-details
                    />
                  </template>
                  <template v-slot:item.message="{ item }">
                    <v-text-field
                      v-model="item.message"
                      :disabled="role !== 'Admin'"
                      dense
                      hide-details
                    />
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn icon :disabled="role !== 'Admin'" @click="saveRule(item)">
                      <v-icon color="green">mdi-content-save</v-icon>
                    </v-btn>
                    <v-btn icon :disabled="role !== 'Admin'" @click="deleteRule(item)">
                      <v-icon color="red">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-tab-item>

              <v-tab-item>
                <v-row class="mt-4">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="recipientAssetId"
                      :items="meterOptions"
                      item-text="text"
                      item-value="value"
                      label="Select meter"
                      dense
                      solo
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-card-title>Email recipients</v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" md="8">
                            <v-text-field v-model="newEmail" label="Add email" dense solo />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-btn color="primary" block :disabled="!recipientAssetId || role !== 'Admin' || !newEmail" @click="addEmailRecipient">
                              Add
                            </v-btn>
                          </v-col>
                        </v-row>
                        <v-data-table :headers="emailHeaders" :items="emailRecipients" dense :loading="loadingRecipients" item-key="rowKey">
                          <template v-slot:item.actions="{ item }">
                            <v-btn icon :disabled="role !== 'Admin'" @click="deleteEmailRecipient(item)">
                              <v-icon color="red">mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-data-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-card-title>LINE groups</v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field v-model="newLineGroupId" label="Group ID" dense solo />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field v-model="newLineNote" label="Note" dense solo />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <v-btn color="primary" :disabled="!recipientAssetId || role !== 'Admin' || !newLineGroupId" @click="addLineGroup">
                              Add LINE group
                            </v-btn>
                            <v-btn outlined class="ml-2" :disabled="!recipientAssetId" @click="testLine">
                              Test send
                            </v-btn>
                          </v-col>
                        </v-row>

                        <v-data-table :headers="lineHeaders" :items="lineGroups" dense :loading="loadingRecipients" item-key="rowKey">
                          <template v-slot:item.actions="{ item }">
                            <v-btn icon :disabled="role !== 'Admin'" @click="deleteLineGroup(item)">
                              <v-icon color="red">mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-data-table>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab-item>
                <v-row class="mt-4">
                  <v-col cols="12" class="d-flex align-center">
                    <div class="text-subtitle-1">Engine run history</div>
                    <v-spacer />
                    <v-btn color="primary" :loading="loadingRuns" @click="loadRuns">Refresh</v-btn>
                  </v-col>
                </v-row>
                <v-data-table :headers="runHeaders" :items="runs" :loading="loadingRuns" dense>
                  <template v-slot:item.runAt="{ item }">
                    {{ $dayjs(item.runAt).local().format('DD MMM YY HH:mm:ss') }}
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </ServicePageLayout>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import ServicePageLayout from '@/components/layout/ServicePageLayout.vue'

export default {
  components: { ServicePageLayout },
  data() {
    return {
      tab: 0,
      loading: false,
      loadingRules: false,
      search: '',
      selectedAssetId: null,
      rules: [],
      watchHeaders: [
        { text: 'Meter', value: 'assetName' },
        { text: 'Monitor', value: 'enabled', width: 120 }
      ],
      ruleHeaders: [
        { text: 'Enabled', value: 'enabled', width: 90 },
        { text: 'Metric', value: 'metric', width: 120 },
        { text: 'Op', value: 'operation', width: 90 },
        { text: 'Threshold', value: 'threshold', width: 120 },
        { text: 'Title', value: 'title' },
        { text: 'Message', value: 'message' },
        { text: 'Actions', value: 'actions', sortable: false, width: 110 }
      ],
      metricOptions: ['demand', 'Unit'],
      operationOptions: ['ge', 'gt', 'le', 'lt', 'eq', 'ne'],
      recipientAssetId: null,
      loadingRecipients: false,
      newEmail: '',
      emailRecipients: [],
      emailHeaders: [
        { text: 'Email', value: 'email' },
        { text: 'Note', value: 'note' },
        { text: 'Actions', value: 'actions', sortable: false, width: 90 }
      ],
      newLineGroupId: '',
      newLineNote: '',
      lineGroups: [],
      lineHeaders: [
        { text: 'Group ID', value: 'groupId' },
        { text: 'Note', value: 'note' },
        { text: 'Actions', value: 'actions', sortable: false, width: 90 }
      ],
      loadingRuns: false,
      runs: [],
      runHeaders: [
        { text: 'Time', value: 'runAt', width: 200 },
        { text: 'Building', value: 'buildingId', width: 260 },
        { text: 'Watched', value: 'watchedMeters', width: 110 },
        { text: 'Alerts created', value: 'alertsCreated', width: 130 },
        { text: 'Duration (ms)', value: 'runMs', width: 130 }
      ]
    }
  },
  computed: {
    role() {
      return this.$store.state.User.role
    },
    allMeters() {
      // Asset.assetEnergy includes power meters already used in energy module
      const meters = this.$store.state.Asset.assetEnergy || []
      return meters.filter((m) => m.assetType === 'PowerMeter')
    },
    watchlistMap() {
      const map = new Map()
      for (const w of (this.$store.state.EnergyAlert.watchlist || [])) {
        map.set(w.rowKey, w.enabled !== false)
      }
      return map
    },
    mergedMeters() {
      return this.allMeters.map((m) => ({
        ...m,
        enabled: this.watchlistMap.get(m.assetId) || false
      }))
    },
    filteredMeters() {
      const s = (this.search || '').toLowerCase()
      if (!s) return this.mergedMeters
      return this.mergedMeters.filter((m) =>
        (m.assetName || '').toLowerCase().includes(s) || (m.assetId || '').toLowerCase().includes(s)
      )
    },
    meterOptions() {
      return this.mergedMeters
        .filter((m) => m.enabled)
        .map((m) => ({ text: `${m.assetName} (${m.assetId})`, value: m.assetId }))
    }
  },
  watch: {
    recipientAssetId(val) {
      if (val) this.loadRecipients(val)
      else {
        this.emailRecipients = []
        this.lineGroups = []
      }
    },
    selectedAssetId(val) {
      if (val) this.loadRules(val)
      else this.rules = []
    }
  },
  mounted() {
    this.refresh()
    this.loadRuns()
  },
  methods: {
    async refresh() {
      this.loading = true
      try {
        await this.$store.dispatch('EnergyAlert/GET_WATCHLIST')
      } finally {
        this.loading = false
      }
    },
    async toggleWatch(item, enabled) {
      await this.$store.dispatch('EnergyAlert/UPSERT_WATCHLIST', {
        assetId: item.assetId,
        enabled: !!enabled,
        label: item.assetName
      })
    },
    async loadRules(assetId) {
      this.loadingRules = true
      try {
        this.rules = await this.$store.dispatch('EnergyAlert/GET_RULES', { assetId })
      } finally {
        this.loadingRules = false
      }
    },
    async addRule() {
      if (!this.selectedAssetId) return
      const payload = {
        assetId: this.selectedAssetId,
        metric: 'demand',
        operation: 'ge',
        threshold: 0,
        title: 'Peak demand high',
        message: 'Please investigate peak demand'
      }
      await this.$store.dispatch('EnergyAlert/CREATE_RULE', payload)
      await this.loadRules(this.selectedAssetId)
    },
    async saveRule(rule) {
      if (!this.selectedAssetId) return
      await this.$store.dispatch('EnergyAlert/UPDATE_RULE', {
        assetId: this.selectedAssetId,
        ruleId: rule.rowKey,
        enabled: rule.enabled !== false,
        metric: rule.metric,
        operation: rule.operation,
        threshold: Number(rule.threshold),
        title: rule.title,
        message: rule.message
      })
      await this.loadRules(this.selectedAssetId)
    },
    async deleteRule(rule) {
      if (!this.selectedAssetId) return
      await this.$store.dispatch('EnergyAlert/DELETE_RULE', {
        assetId: this.selectedAssetId,
        ruleId: rule.rowKey,
        metric: rule.metric,
        operation: rule.operation,
        threshold: Number(rule.threshold)
      })
      await this.loadRules(this.selectedAssetId)
    },
    async loadRecipients(assetId) {
      this.loadingRecipients = true
      try {
        this.emailRecipients = await this.$store.dispatch('Email/GET_EMAIL_BY_DEVICE', { assetId })
        this.lineGroups = await this.$store.dispatch('Notify/GET_TOKEN_BY_DEVICE', { assetId })
      } finally {
        this.loadingRecipients = false
      }
    },
    async addEmailRecipient() {
      if (!this.recipientAssetId) return
      const data = {
        assetId: this.recipientAssetId,
        id: uuidv4(),
        email: this.newEmail,
        note: 'Energy alert'
      }
      await this.$store.dispatch('Email/UPDATE_EMAIL_BY_DEVICE', data)
      this.newEmail = ''
      await this.loadRecipients(this.recipientAssetId)
    },
    async deleteEmailRecipient(item) {
      await this.$store.dispatch('Email/DELETE_EMAIL_BY_DEVICE', {
        assetId: this.recipientAssetId,
        id: item.rowKey
      })
      await this.loadRecipients(this.recipientAssetId)
    },
    async addLineGroup() {
      if (!this.recipientAssetId) return
      const data = {
        assetId: this.recipientAssetId,
        id: uuidv4(),
        groupId: this.newLineGroupId,
        note: this.newLineNote || 'Energy alert'
      }
      await this.$store.dispatch('Notify/UPDATE_TOKEN_BY_DEVICE', data)
      this.newLineGroupId = ''
      this.newLineNote = ''
      await this.loadRecipients(this.recipientAssetId)
    },
    async deleteLineGroup(item) {
      await this.$store.dispatch('Notify/DELETE_TOKEN_BY_DEVICE', {
        assetId: this.recipientAssetId,
        partitionKey: this.recipientAssetId,
        rowKey: item.rowKey
      })
      await this.loadRecipients(this.recipientAssetId)
    },
    async testLine() {
      if (!this.recipientAssetId) return
      if (!this.newLineGroupId && this.lineGroups.length === 0) return
      const groupId = this.newLineGroupId || this.lineGroups[0].groupId
      await this.$store.dispatch('Notify/SEND_LINE', { groupId })
    },
    async loadRuns() {
      this.loadingRuns = true
      try {
        this.runs = await this.$store.dispatch('EnergyAlert/GET_ENGINE_RUNS')
      } finally {
        this.loadingRuns = false
      }
    }
  }
}
</script>

